import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import ScrollProgress from "@/components/site/ScrollProgress";
import MobileStickyCTA from "@/components/site/MobileStickyCTA";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { OG_IMAGES, SITE_URL} from "@/lib/seo";
import {
  wearableHeroLeft,
  wearableHeroRight,
  sectionTitleFade,
  listItemFade,
  fadeUpInViewStaggered,
} from "./_partials/animationVariants";

const WEARABLES_BANNER =
  "https://customer-assets.emergentagent.com/job_integrated-matrix/artifacts/4kmwsjtk_data%202.avif";

const PLATFORMS = [
  {
    name: "Apple Health",
    role: "iOS health hub",
    signals: "Steps · workouts · heart rate · HRV · sleep · ECG · VO₂ max · respiratory rate",
  },
  {
    name: "Oura",
    role: "Recovery & sleep ring",
    signals: "Sleep stages · readiness · HRV · body temperature · resting heart rate",
  },
  {
    name: "Whoop",
    role: "Strain & recovery band",
    signals: "Strain · recovery · HRV · respiratory rate · sleep performance",
  },
  {
    name: "Fitbit",
    role: "Daily activity & wellness",
    signals: "Steps · heart rate · sleep · active zone minutes · stress management",
  },
  {
    name: "Garmin",
    role: "Performance & endurance",
    signals: "VO₂ max · training load · recovery · pulse-ox · stress · body battery",
  },
];

const SYNC_PIPELINE = [
  {
    label: "Connect",
    body: "One-tap, OAuth-secured links to your existing wearable accounts. No new devices to buy. No data to manually upload.",
  },
  {
    label: "Sync",
    body: "Signals stream into your secure Integrated Wellness patient record — continuously, in the background, HIPAA-aligned.",
  },
  {
    label: "Synthesize",
    body: "Wearable data is fused with your labs, DNA, microbiome, and biometrics in the Integrated Matrix Engine — translated into clinical protocols.",
  },
  {
    label: "Surface",
    body: "Your care team sees the signals that matter — earlier — so adjustments to your plan happen in days, not annual visits.",
  },
];

export default function WearableIntegrations() {
  useDocumentMeta({
    title: "Wearable Integrations — Sync Your Data | Integrated Clinical Intelligence",
    description:
      "Native integrations with Apple Health, Oura, Whoop, Fitbit, and Garmin. Sync your wearable data with our HIPAA-aligned patient database and fuse it with labs, DNA, microbiome, and biometrics.",
    image: OG_IMAGES.wearableIntegrations,
    url: `${SITE_URL}/wearable-integrations`,
  });

  return (
    <div data-testid="wearable-integrations-page" className="min-h-screen bg-background">
      <ScrollProgress />
      <Header />

      <main className="pt-28 md:pt-32 pb-24">
        <section className="max-w-6xl mx-auto px-4 md:px-8">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 font-mono text-[0.65rem] uppercase tracking-[0.32em] text-accent hover:text-primary transition-colors"
          >
            ← Back to Integrated Clinical Intelligence
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <motion.div {...wearableHeroLeft} className="lg:col-span-6">
              <div className="iw-overline mb-5">Native Integrations
              </div>
              <h1 className="font-display font-bold tracking-[-0.04em] leading-[1.0] text-[clamp(2.5rem,5.5vw,4.75rem)]">
                Sync your wearable data with{" "}
                <em className="not-italic text-primary">our patient database</em>.
              </h1>
              <p className="mt-6 text-xl md:text-2xl text-foreground/80 leading-relaxed">
                Every signal from every device — Apple Health, Oura, Whoop, Fitbit, Garmin — flowing
                into a single, secure patient record and fused with your labs, DNA, microbiome,
                and biometrics.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a  href={process.env.REACT_APP_JOTFORM_INTAKE_URL} data-testid="wearable-cta-intake"
                  className="iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]">
                  Begin Intake <span aria-hidden>→</span>
                </a>
                <Link
                  to="/agents/rhea"
                  data-testid="wearable-cta-rhea"
                  className="iw-btn-ghost inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]"
                >
                  Talk to Rhea <span aria-hidden className="text-primary">→</span>
                </Link>
              </div>
            </motion.div>

            <motion.div {...wearableHeroRight} className="lg:col-span-6">
              <div className="relative aspect-square rounded-[2rem] overflow-hidden border-2 border-primary/25 shadow-xl bg-foreground">
                <img
                  src={WEARABLES_BANNER}
                  alt="Native integrations with Apple Health, Oura, Whoop, Fitbit, and Garmin — every wearable signal synced into one patient record"
                  className="w-full h-full object-cover"
                  loading="eager"
                  style={{
                    filter:
                      "hue-rotate(200deg) saturate(1.05) brightness(0.9) contrast(1.05)",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 via-transparent to-transparent mix-blend-multiply" />
                <div className="absolute inset-0 bg-gradient-to-tl from-primary/15 via-transparent to-transparent" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Supported Platforms */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <motion.div {...sectionTitleFade}>
            <div className="iw-overline mb-4">Supported Platforms
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl">
              The wearables that{" "}
              <em className="not-italic text-primary">already live on your wrist</em>.
            </h2>
            <p className="mt-5 max-w-2xl text-base md:text-lg text-foreground/75 leading-relaxed">
              No new hardware. No new accounts. We connect to the platforms millions of patients
              already use — and the list keeps growing.
            </p>
          </motion.div>

          <ul className="mt-12 divide-y divide-border border-y border-border">
            {PLATFORMS.map((p, i) => (
              <motion.li
                key={p.name}
                {...listItemFade(i)}
                data-testid={`platform-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-8"
              >
                <div className="col-span-2 md:col-span-1 font-mono text-xs md:text-sm tracking-[0.18em] text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-10 md:col-span-4">
                  <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-1.5">
                    {p.role}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight leading-tight">
                    {p.name}
                  </h3>
                </div>
                <p className="col-span-12 md:col-span-7 text-base text-foreground/75 leading-relaxed">
                  {p.signals}
                </p>
              </motion.li>
            ))}
          </ul>
        </section>

        {/* Sync pipeline */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <motion.div {...sectionTitleFade}>
            <div className="iw-overline mb-4">How It Syncs
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight max-w-3xl">
              From your wrist to your{" "}
              <em className="not-italic text-primary">clinical record</em>.
            </h2>
          </motion.div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {SYNC_PIPELINE.map((s, i) => (
              <motion.div
                key={s.label}
                {...fadeUpInViewStaggered(i * 0.06)}
                data-testid={`sync-step-${i + 1}`}
                className="premium-card p-6 md:p-7 bg-card border-2 border-border rounded-2xl hover:border-primary/45 transition-colors"
              >
                <div className="font-mono text-[0.6rem] uppercase tracking-[0.22em] text-primary/75 mb-3">
                  Step 0{i + 1}
                </div>
                <h3 className="font-display text-2xl font-semibold leading-tight mb-3">
                  {s.label}
                </h3>
                <p className="text-base text-foreground/75 leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Security & Compliance */}
        <section className="max-w-6xl mx-auto px-4 md:px-8 mt-24 md:mt-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <motion.div {...sectionTitleFade} className="lg:col-span-5">
              <div className="iw-overline mb-4">Built for Trust
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
                Your data, <em className="not-italic text-primary">your record</em>.
              </h2>
              <p className="mt-5 text-base md:text-lg text-foreground/75 leading-relaxed">
                Every integration is HIPAA-aligned and built on the same enterprise infrastructure
                that powers our partners — Healthie EHR, Keragon workflow automation, and Spruce
                patient communication.
              </p>
            </motion.div>

            <motion.ul
              {...fadeUpInViewStaggered(0.1)}
              className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3"
            >
              {[
                "HIPAA-aligned data handling",
                "OAuth 2.0 secured connections",
                "Encrypted in transit & at rest",
                "Revoke access anytime",
                "Audit logs on every sync",
                "No data sold to advertisers — ever",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 p-4 rounded-xl border border-border bg-card"
                >
                  <CheckCircle
                    size={22}
                    weight="duotone"
                    className="text-primary flex-shrink-0 mt-0.5"
                  />
                  <span className="text-base text-foreground/85 leading-snug">{item}</span>
                </li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-3xl mx-auto px-4 md:px-8 mt-24 md:mt-32 text-center">
          <motion.div {...sectionTitleFade}>
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight leading-tight">
              Connect your wearables in <em className="not-italic text-primary">minutes</em>.
            </h2>
            <p className="mt-5 text-lg md:text-xl text-foreground/75 leading-relaxed">
              Start your medical wellness intake — we'll handle the rest.
            </p>
            <a  href={process.env.REACT_APP_JOTFORM_INTAKE_URL} data-testid="wearable-closing-cta"
              className="mt-8 iw-btn-red inline-flex items-center gap-2 px-8 py-4 text-[0.75rem]">
              Begin Intake <ArrowSquareOut size={18} weight="bold" aria-hidden />
            </a>
          </motion.div>
        </section>
      </main>

      <Footer />
      <MobileStickyCTA />
    </div>
  );
}
